import { useState, useMemo } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { getInitials } from '@/lib/utils';
import { useComments, useCreateComment } from '../hooks/useComments';
import { useOwnerOptions } from '../hooks/useRocks';
import type { OwnerOption } from '../api';

interface CommentThreadProps {
  entityType: string;
  entityId: string;
}

function resolveAuthor(authorId: string, ownerMap: Map<string, OwnerOption>) {
  const owner = ownerMap.get(authorId);
  const name = owner?.name || owner?.email || 'Unknown user';
  const initials = owner ? getInitials(name) : authorId.slice(-2).toUpperCase();
  return { name, initials };
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

export function CommentThread({ entityType, entityId }: CommentThreadProps) {
  const [body, setBody] = useState('');
  const { data: comments = [], isLoading } = useComments(entityType, entityId);
  const { data: owners = [] } = useOwnerOptions();
  const createComment = useCreateComment();

  const ownerMap = useMemo(() => {
    const map = new Map<string, OwnerOption>();
    owners.forEach((o) => {
      map.set(o.id, o);
      if (o.mongoUserId) map.set(o.mongoUserId, o);
    });
    return map;
  }, [owners]);

  const handleSubmit = () => {
    const text = body.trim();
    if (!text) return;
    createComment.mutate(
      { entity_type: entityType, entity_id: entityId, body: text },
      { onSuccess: () => setBody('') }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="space-y-3">
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-10 rounded bg-muted animate-pulse" />
          ))}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-xs text-muted-foreground py-1">No comments yet.</p>
      ) : (
        <div className="space-y-3">
          {comments.map((comment) => {
            const author = resolveAuthor(comment.authorId, ownerMap);
            return (
              <div key={comment.id} className="flex gap-2.5">
                <div className="shrink-0 w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                  {author.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-medium">{author.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(comment.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">{comment.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Input area */}
      <div className="flex gap-2 items-end pt-1">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a comment... (Cmd+Enter to send)"
          className="min-h-[60px] text-sm resize-none"
          rows={2}
        />
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!body.trim() || createComment.isPending}
          className="shrink-0 h-9"
        >
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
